import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import {
  DocumentAddWebsiteBodySchema,
  DocumentCreateBodySchema,
  // Folders
  DocumentCreateFolderBodySchema,
  DocumentCreateGoogleSheetBodySchema,
  DocumentCreateGoogleSheetResponseSchema,
  DocumentCreateTextBodySchema,
  DocumentDeletePathVarsSchema,
  DocumentDisableSyncPathVarsSchema,
  DocumentDisableSyncResponseSchema,
  DocumentDownloadUrlPathVarsSchema,
  DocumentDownloadUrlResponseSchema,
  DocumentEnableSyncBodySchema,
  // Sync
  DocumentEnableSyncPathVarsSchema,
  DocumentEnableSyncResponseSchema,
  DocumentGetFolderContentsPathVarsSchema,
  DocumentGetFolderContentsResponseSchema,
  DocumentGetFolderPathPathVarsSchema,
  DocumentGetFolderPathResponseSchema,
  DocumentGetOrganizationSyncStatusResponseSchema,
  DocumentGetPathVarsSchema,
  DocumentGetProviderTokenPathVarsSchema,
  DocumentGetProviderTokenResponseSchema,
  DocumentGetStatisticsResponseSchema,
  DocumentGetSubfoldersParamsSchema,
  DocumentGetSubfoldersResponseSchema,
  DocumentGetSyncStatusPathVarsSchema,
  DocumentGetSyncStatusResponseSchema,
  // Google Drive
  DocumentGoogleDriveAuthBodySchema,
  DocumentGoogleDriveAuthResponseSchema,
  DocumentGoogleSheetColumnsBodySchema,
  DocumentGoogleSheetColumnsResponseSchema,
  DocumentListGoogleDriveFilesParamsSchema,
  DocumentListGoogleDriveFilesResponseSchema,
  // Core CRUD
  DocumentListParamsSchema,
  DocumentListResponseSchema,
  DocumentListSharePointFilesParamsSchema,
  DocumentListSharePointFilesResponseSchema,
  DocumentListSharePointSitesParamsSchema,
  DocumentListSharePointSitesResponseSchema,
  DocumentMoveBodySchema,
  DocumentMovePathVarsSchema,
  // Notion
  DocumentNotionAuthBodySchema,
  DocumentNotionAuthResponseSchema,
  DocumentPreviewUrlPathVarsSchema,
  DocumentPreviewUrlResponseSchema,
  // Misc
  DocumentProcessPathVarsSchema,
  DocumentProcessResponseSchema,
  DocumentRefreshGoogleDriveTokenBodySchema,
  DocumentRefreshGoogleDriveTokenResponseSchema,
  DocumentRefreshSharePointTokenBodySchema,
  DocumentRefreshSharePointTokenResponseSchema,
  // Responses
  DocumentResponseSchema,
  DocumentSearchNotionParamsSchema,
  DocumentSearchNotionResponseSchema,
  // SharePoint
  DocumentSharePointAuthBodySchema,
  DocumentSharePointAuthResponseSchema,
  DocumentTestQueryBodySchema,
  DocumentTestQueryPathVarsSchema,
  DocumentTestQueryResponseSchema,
  DocumentTriggerSyncPathVarsSchema,
  DocumentTriggerSyncResponseSchema,
  DocumentUpdateBodySchema,
  DocumentUpdatePathVarsSchema,
  DocumentUpdateSyncIntervalBodySchema,
  DocumentUpdateSyncIntervalPathVarsSchema,
  DocumentUpdateSyncIntervalResponseSchema,
  // Uploads
  DocumentUploadFileBodySchema,
  DocumentUploadGoogleDriveBodySchema,
  DocumentUploadGoogleDriveResponseSchema,
  DocumentUploadNotionContentBodySchema,
  DocumentUploadNotionContentResponseSchema,
  DocumentUploadSharePointBodySchema,
  DocumentUploadSharePointResponseSchema
} from "./schemas";

/**
 * Zod API Validation Schemas for Documents
 *
 * Single source of truth:
 * - Defines validation rules
 * - Generates TApi types
 * - Used for runtime validation (backend + frontend)
 *
 * Naming: Z prefix = Zod schemas (vs DbEntity = Prisma, TApi = TypeScript types)
 */
export const ZDocuments = {
  // ============================================================================
  // CORE CRUD OPERATIONS
  // ============================================================================

  List: {
    method: "GET" as const,
    path: "/api/documents",
    Params: DocumentListParamsSchema,
    Response: ApiSuccessSchema(DocumentListResponseSchema)
  },
  Get: {
    method: "GET" as const,
    path: "/api/documents/:id",
    PathVars: DocumentGetPathVarsSchema,
    Response: ApiSuccessSchema(DocumentResponseSchema)
  },
  Create: {
    method: "POST" as const,
    path: "/api/documents",
    Body: DocumentCreateBodySchema,
    Response: ApiSuccessSchema(DocumentResponseSchema)
  },
  Update: {
    method: "PUT" as const,
    path: "/api/documents/:id",
    PathVars: DocumentUpdatePathVarsSchema,
    Body: DocumentUpdateBodySchema,
    Response: ApiSuccessSchema(DocumentResponseSchema)
  },
  Delete: {
    method: "DELETE" as const,
    path: "/api/documents/:id",
    PathVars: DocumentDeletePathVarsSchema,
    Response: ApiSuccessSchema(z.void())
  },

  // ============================================================================
  // FOLDER OPERATIONS
  // ============================================================================

  CreateFolder: {
    method: "POST" as const,
    path: "/api/documents/upload/folder",
    Body: DocumentCreateFolderBodySchema,
    Response: ApiSuccessSchema(DocumentResponseSchema)
  },
  GetSubfolders: {
    method: "GET" as const,
    path: "/api/documents/subfolders",
    Params: DocumentGetSubfoldersParamsSchema,
    Response: ApiSuccessSchema(DocumentGetSubfoldersResponseSchema)
  },
  GetFolderContents: {
    method: "GET" as const,
    path: "/api/documents/:id/children",
    PathVars: DocumentGetFolderContentsPathVarsSchema,
    Response: ApiSuccessSchema(DocumentGetFolderContentsResponseSchema)
  },
  GetFolderPath: {
    method: "GET" as const,
    path: "/api/documents/:id/path",
    PathVars: DocumentGetFolderPathPathVarsSchema,
    Response: ApiSuccessSchema(DocumentGetFolderPathResponseSchema)
  },

  // ============================================================================
  // UPLOAD OPERATIONS
  // ============================================================================

  UploadFile: {
    method: "POST" as const,
    path: "/api/documents/upload/file",
    Body: DocumentUploadFileBodySchema,
    Response: ApiSuccessSchema(DocumentResponseSchema)
  },
  CreateText: {
    method: "POST" as const,
    path: "/api/documents/upload/text",
    Body: DocumentCreateTextBodySchema,
    Response: ApiSuccessSchema(DocumentResponseSchema)
  },
  AddWebsite: {
    method: "POST" as const,
    path: "/api/documents/upload/website",
    Body: DocumentAddWebsiteBodySchema,
    Response: ApiSuccessSchema(DocumentResponseSchema)
  },
  CreateGoogleSheet: {
    method: "POST" as const,
    path: "/api/documents/upload/google-sheet",
    Body: DocumentCreateGoogleSheetBodySchema,
    Response: ApiSuccessSchema(DocumentCreateGoogleSheetResponseSchema)
  },
  GoogleSheetColumns: {
    method: "POST" as const,
    path: "/api/documents/upload/google-sheet/columns",
    Body: DocumentGoogleSheetColumnsBodySchema,
    Response: ApiSuccessSchema(DocumentGoogleSheetColumnsResponseSchema)
  },

  // ============================================================================
  // GOOGLE DRIVE CONNECTION
  // ============================================================================

  GoogleDriveAuth: {
    method: "POST" as const,
    path: "/api/documents/upload/google-drive/auth",
    Body: DocumentGoogleDriveAuthBodySchema,
    Response: ApiSuccessSchema(DocumentGoogleDriveAuthResponseSchema)
  },
  ListGoogleDriveFiles: {
    method: "GET" as const,
    path: "/api/documents/upload/google-drive/files",
    Params: DocumentListGoogleDriveFilesParamsSchema,
    Response: ApiSuccessSchema(DocumentListGoogleDriveFilesResponseSchema)
  },
  UploadGoogleDrive: {
    method: "POST" as const,
    path: "/api/documents/upload/google-drive",
    Body: DocumentUploadGoogleDriveBodySchema,
    Response: ApiSuccessSchema(DocumentUploadGoogleDriveResponseSchema)
  },
  RefreshGoogleDriveToken: {
    method: "POST" as const,
    path: "/api/documents/upload/google-drive/refresh",
    Body: DocumentRefreshGoogleDriveTokenBodySchema,
    Response: ApiSuccessSchema(DocumentRefreshGoogleDriveTokenResponseSchema)
  },

  // ============================================================================
  // SHAREPOINT CONNECTION
  // ============================================================================

  SharePointAuth: {
    method: "POST" as const,
    path: "/api/documents/upload/sharepoint/auth",
    Body: DocumentSharePointAuthBodySchema,
    Response: ApiSuccessSchema(DocumentSharePointAuthResponseSchema)
  },
  ListSharePointSites: {
    method: "GET" as const,
    path: "/api/documents/upload/sharepoint/sites",
    Params: DocumentListSharePointSitesParamsSchema,
    Response: ApiSuccessSchema(DocumentListSharePointSitesResponseSchema)
  },
  ListSharePointFiles: {
    method: "GET" as const,
    path: "/api/documents/upload/sharepoint/files",
    Params: DocumentListSharePointFilesParamsSchema,
    Response: ApiSuccessSchema(DocumentListSharePointFilesResponseSchema)
  },
  UploadSharePoint: {
    method: "POST" as const,
    path: "/api/documents/upload/sharepoint",
    Body: DocumentUploadSharePointBodySchema,
    Response: ApiSuccessSchema(DocumentUploadSharePointResponseSchema)
  },
  RefreshSharePointToken: {
    method: "POST" as const,
    path: "/api/documents/upload/sharepoint/refresh",
    Body: DocumentRefreshSharePointTokenBodySchema,
    Response: ApiSuccessSchema(DocumentRefreshSharePointTokenResponseSchema)
  },

  // ============================================================================
  // NOTION CONNECTION
  // ============================================================================

  NotionAuth: {
    method: "POST" as const,
    path: "/api/documents/upload/notion/auth",
    Body: DocumentNotionAuthBodySchema,
    Response: ApiSuccessSchema(DocumentNotionAuthResponseSchema)
  },
  SearchNotion: {
    method: "GET" as const,
    path: "/api/documents/upload/notion/search",
    Params: DocumentSearchNotionParamsSchema,
    Response: ApiSuccessSchema(DocumentSearchNotionResponseSchema)
  },
  UploadNotionContent: {
    method: "POST" as const,
    path: "/api/documents/upload/notion",
    Body: DocumentUploadNotionContentBodySchema,
    Response: ApiSuccessSchema(DocumentUploadNotionContentResponseSchema)
  },

  // ============================================================================
  // SYNC OPERATIONS
  // ============================================================================

  EnableSync: {
    method: "POST" as const,
    path: "/api/documents/:id/sync/enable",
    PathVars: DocumentEnableSyncPathVarsSchema,
    Body: DocumentEnableSyncBodySchema,
    Response: ApiSuccessSchema(DocumentEnableSyncResponseSchema)
  },
  DisableSync: {
    method: "POST" as const,
    path: "/api/documents/:id/sync/disable",
    PathVars: DocumentDisableSyncPathVarsSchema,
    Response: ApiSuccessSchema(DocumentDisableSyncResponseSchema)
  },
  TriggerSync: {
    method: "POST" as const,
    path: "/api/documents/:id/sync/trigger",
    PathVars: DocumentTriggerSyncPathVarsSchema,
    Response: ApiSuccessSchema(DocumentTriggerSyncResponseSchema)
  },
  GetSyncStatus: {
    method: "GET" as const,
    path: "/api/documents/:id/sync/status",
    PathVars: DocumentGetSyncStatusPathVarsSchema,
    Response: ApiSuccessSchema(DocumentGetSyncStatusResponseSchema)
  },
  UpdateSyncInterval: {
    method: "POST" as const,
    path: "/api/documents/:id/sync/interval",
    PathVars: DocumentUpdateSyncIntervalPathVarsSchema,
    Body: DocumentUpdateSyncIntervalBodySchema,
    Response: ApiSuccessSchema(DocumentUpdateSyncIntervalResponseSchema)
  },
  GetOrganizationSyncStatus: {
    method: "GET" as const,
    path: "/api/documents/sync/status",
    Response: ApiSuccessSchema(DocumentGetOrganizationSyncStatusResponseSchema)
  },

  // ============================================================================
  // MISCELLANEOUS OPERATIONS
  // ============================================================================

  Process: {
    method: "POST" as const,
    path: "/api/documents/:id/process",
    PathVars: DocumentProcessPathVarsSchema,
    Response: ApiSuccessSchema(DocumentProcessResponseSchema)
  },
  Move: {
    method: "POST" as const,
    path: "/api/documents/:id/move",
    PathVars: DocumentMovePathVarsSchema,
    Body: DocumentMoveBodySchema,
    Response: ApiSuccessSchema(DocumentResponseSchema)
  },
  GetStatistics: {
    method: "GET" as const,
    path: "/api/documents/statistics",
    Response: ApiSuccessSchema(DocumentGetStatisticsResponseSchema)
  },
  TestQuery: {
    method: "POST" as const,
    path: "/api/documents/:id/test-query",
    PathVars: DocumentTestQueryPathVarsSchema,
    Body: DocumentTestQueryBodySchema,
    Response: ApiSuccessSchema(DocumentTestQueryResponseSchema)
  },
  DownloadUrl: {
    method: "GET" as const,
    path: "/api/documents/:id/download-url",
    PathVars: DocumentDownloadUrlPathVarsSchema,
    Response: ApiSuccessSchema(DocumentDownloadUrlResponseSchema)
  },
  PreviewUrl: {
    method: "GET" as const,
    path: "/api/documents/:id/preview-url",
    PathVars: DocumentPreviewUrlPathVarsSchema,
    Response: ApiSuccessSchema(DocumentPreviewUrlResponseSchema)
  },
  GetProviderToken: {
    method: "GET" as const,
    path: "/api/documents/connections/:service/token",
    PathVars: DocumentGetProviderTokenPathVarsSchema,
    Response: ApiSuccessSchema(DocumentGetProviderTokenResponseSchema)
  }
} as const;
